import React, { useEffect, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { differenceInMinutes, format } from "date-fns"
import { Loader2, Plus } from "lucide-react"
import { toast } from "sonner"

import RoleTeamGuard from "@/components/guards/RoleTeamGuard"
import { BlockSection } from "@/components/sheriff/ui/BlockSection"
import { Button } from "@/components/ui/button"
import { INVALID_UK_TIME_ERROR, ukDateOf, ukWallClockToInstant } from "@/constants/sheriffOptions"
import { useAuth } from "@/contexts/AuthContext"
import { withCancellationAuditComment, withUpdateAuditComment } from "@/hooks/useShiftAuditComment"
import { buildBlockDetailsDiff } from "@/lib/shiftSaveDiff"
import { type CancelShiftBody, type Shift, shiftsService } from "@/services/shiftsService"
import { Person } from "@/types/person"
import { formatTo24HourTime } from "@/utils/timeFormat"

import AddShiftDialog, { NewShift } from "../dialogs/AddShiftDialog"
import BlockDetailsDialog, { BlockDetails } from "../dialogs/BlockDetailsDialog"

interface SheriffTabProps {
  person: Person
}

function toBlockDetails(shift: Shift): BlockDetails {
  return {
    id: shift.id,
    startDate: ukDateOf(new Date(shift.startTime)),
    endDate: ukDateOf(new Date(shift.endTime)),
    startTime: formatTo24HourTime(shift.startTime),
    endTime: formatTo24HourTime(shift.endTime),
    realStartTime: formatTo24HourTime(shift.timesheet?.clockedInTime),
    realEndTime: formatTo24HourTime(shift.timesheet?.clockedOutTime),
    area: shift.area || "",
    type: shift.type || "",
    role: shift.employer || "",
    location: shift.location,
    notes: shift.notes || undefined,
    cancelReason: shift.cancelledReason as BlockDetails["cancelReason"],
  }
}

const SheriffTab: React.FC<SheriffTabProps> = ({ person }) => {
  const queryClient = useQueryClient()
  const { claims } = useAuth()
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [upcomingShifts, setUpcomingShifts] = useState<Shift[]>([])
  const [pastShifts, setPastShifts] = useState<Shift[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const teamPermissions = claims.team_permissions || []
  const canEditSheriff = teamPermissions.includes("update.tab.sheriff")

  // Fetch shifts on mount
  useEffect(() => {
    const fetchShifts = async () => {
      if (!person.id) return

      setIsLoading(true)
      try {
        const { upcoming, past } = await shiftsService.fetchPersonShifts(person.person_id)

        setUpcomingShifts(upcoming)
        setPastShifts(past)
      } catch (error) {
        console.error("Failed to fetch shifts:", error)
        toast.error("Failed to load blocks")
      } finally {
        setIsLoading(false)
      }
    }

    fetchShifts()
  }, [person])

  const handleShiftCreated = async (newShift: NewShift) => {
    try {
      // Validation
      if (!newShift.location) {
        throw new Error("Please select a location")
      }
      if (!newShift.area) {
        throw new Error("Please select an area")
      }

      const startDateTime = ukWallClockToInstant(newShift.startDate, newShift.startTime)
      const endDateTime = ukWallClockToInstant(newShift.endDate, newShift.endTime)
      if (Number.isNaN(startDateTime.getTime()) || Number.isNaN(endDateTime.getTime())) {
        throw new Error(INVALID_UK_TIME_ERROR)
      }

      await shiftsService.createShift({
        startTime: startDateTime.toISOString(),
        endTime: endDateTime.toISOString(),
        area: newShift.area,
        type: person.sheriff_type || "HivedVan",
        employer: person.employer || "Hived",
        location: newShift.location,
        depotId: newShift.depotId ?? null,
        notes: newShift.notes || null,
        peopleId: person.id,
        notifications: true,
      })

      // Refresh shifts
      const { upcoming } = await shiftsService.fetchPersonShifts(person.person_id)
      setUpcomingShifts(upcoming)

      await queryClient.invalidateQueries({ queryKey: ["shifts"] })
      toast.success("Block created successfully")
    } catch (error) {
      console.error("Failed to create shift:", error)
      const errorMessage = error instanceof Error ? error.message : "Failed to create block"
      toast.error(errorMessage)
      throw error // Re-throw so dialog knows to stay open
    }
  }

  const [detailsOpen, setDetailsOpen] = React.useState(false)
  const [detailsMode, setDetailsMode] = React.useState<"view" | "edit">("view")
  const [selected, setSelected] = React.useState<Shift | null>(null)

  const openView = (s: Shift) => {
    setSelected(s)
    setDetailsMode("view")
    setDetailsOpen(true)
  }

  const openEdit = (s: Shift) => {
    setSelected(s)
    setDetailsMode("edit")
    setDetailsOpen(true)
  }

  const saveDetails = async (updated: BlockDetails) => {
    try {
      if (updated.cancelReason) {
        const body: CancelShiftBody = {
          id: updated.id,
          cancelledReason: updated.cancelReason,
        }
        const cancelBody = await withCancellationAuditComment(body, {
          skipPrompt: updated.type !== "Internal",
        })
        await shiftsService.cancelShift(cancelBody)
      } else {
        const { hasChanges, updateBody: body } = buildBlockDetailsDiff(
          updated,
          selected ? toBlockDetails(selected) : updated,
        )

        if (!hasChanges) {
          toast.info("No changes to save")
          return
        }

        const updateBody = await withUpdateAuditComment(body, {
          skipPrompt: updated.type !== "Internal",
        })
        await shiftsService.updateShift(updateBody)
      }

      // Refresh shifts
      const { upcoming, past } = await shiftsService.fetchPersonShifts(person.person_id)
      setUpcomingShifts(upcoming)
      setPastShifts(past)

      await queryClient.invalidateQueries({ queryKey: ["shifts"] })
      toast.success(
        updated.cancelReason ? "Block cancelled successfully" : "Block updated successfully",
      )
    } catch (error) {
      console.error("Failed to update shift:", error)
      const errorMessage = error instanceof Error ? error.message : "Failed to update block"
      toast.error(errorMessage)
      throw error
    }
  }

  // ----- Summary calculations -----
  const minutesBetween = (start?: string | null, end?: string | null) => {
    if (!start || !end) return 0
    return Math.max(0, differenceInMinutes(new Date(end), new Date(start)))
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-palette-jungleGreen400" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Blocks</h3>
        <RoleTeamGuard roles={["Admin", "Manager"]}>
          {canEditSheriff && (
            <Button size="sm" onClick={() => setShowAddDialog(true)}>
              {" "}
              <Plus className="h-4 w-4 mr-2" /> Create block
            </Button>
          )}
        </RoleTeamGuard>
      </div>
      {/* Summary callout - moved below header, with date range + insights */}
      <div className="rounded-md border border-blue-200 bg-blue-50 p-4">
        <div className="flex flex-col gap-3">
          <div className="text-sm font-medium text-blue-900">Last 7 Days Summary</div>
          <div className="text-xs text-blue-900/80">
            {format(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), "EEE, d MMMM yyyy")} –{" "}
            {format(new Date(), "EEE, d MMMM yyyy")}
          </div>
          {(() => {
            const now = Date.now()
            const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000
            const sevenDaysAhead = now + 7 * 24 * 60 * 60 * 1000
            const past7 = pastShifts.filter((s) => {
              const t = new Date(s.startTime).getTime()
              return t >= sevenDaysAgo && t <= now
            })
            const pastMin = past7.reduce(
              (acc, s) =>
                acc + minutesBetween(s.timesheet?.clockedInTime, s.timesheet?.clockedOutTime),
              0,
            )
            const pastAvgMin = past7.length ? Math.round(pastMin / past7.length) : 0
            const upcoming7 = upcomingShifts.filter(
              (s) => new Date(s.startTime).getTime() <= sevenDaysAhead,
            )
            const plannedMin = upcoming7.reduce(
              (acc, s) => acc + minutesBetween(s.startTime, s.endTime),
              0,
            )
            const plannedAvgMin = upcoming7.length ? Math.round(plannedMin / upcoming7.length) : 0
            const fmtHm = (mins: number) => {
              const h = Math.floor(mins / 60)
              const m = mins % 60
              return `${h}h${m ? ` ${m}m` : ""}`
            }
            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                {/* Row 1 */}
                <div className="flex items-center justify-between">
                  <span className="text-blue-900/80">Total Block:</span>
                  <span className="font-medium text-blue-900">{past7.length}</span>
                </div>
                <div>{/* intentionally blank */}</div>
                {/* Row 2 */}
                <div className="flex items-center justify-between">
                  <span className="text-blue-900/80">Total Hours:</span>
                  <span className="font-medium text-blue-900">{fmtHm(pastMin)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-blue-900/80">Total Hours Planned:</span>
                  <span className="font-medium text-blue-900">{fmtHm(plannedMin)}</span>
                </div>
                {/* Row 3 */}
                <div className="flex items-center justify-between">
                  <span className="text-blue-900/80">Avg Hours:</span>
                  <span className="font-medium text-blue-900">{fmtHm(pastAvgMin)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-blue-900/80">Avg Hours Planned:</span>
                  <span className="font-medium text-blue-900">{fmtHm(plannedAvgMin)}</span>
                </div>
              </div>
            )
          })()}
        </div>
      </div>
      <BlockSection
        tone="upcoming"
        shifts={upcomingShifts}
        canEdit={canEditSheriff}
        onEdit={openEdit}
        onView={openView}
      />

      <BlockSection tone="past" shifts={pastShifts} canEdit={false} onView={openView} />
      <AddShiftDialog
        person={person}
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onShiftCreated={handleShiftCreated}
      />
      <BlockDetailsDialog
        mode={canEditSheriff ? detailsMode : "view"}
        block={selected ? toBlockDetails(selected) : null}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        onSave={saveDetails}
        person={person}
      />
    </div>
  )
}

export default SheriffTab
